import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Search } from "lucide-react";
import UIModal from "../UIModal/UIModal";
import { UIButton } from "../UIButton";
import Spinner from "../Spinner";
import { theme } from "@src/theme";
import { ITrack } from "@src/types/track";
import {
  useUpdateTrackMutation,
  useTrackShazamInfoQuery,
} from "@src/queries/trackQueries";
import { useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "@src/queries/queryKeys";
import { useNotification } from "@src/context/NotificationContext";

export type TrackShazamModalProps = {
  isOpen: boolean;
  onClose: () => void;
  track: ITrack | null;
};

const SHAZAM_POLL_INTERVAL_MS = 3000;
const SHAZAM_POLL_TIMEOUT_MS = 60000;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.margins.medium};
  width: 320px;
  max-width: 100%;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.margins.xsmall};
`;

const FieldLabel = styled.span`
  font-size: 0.8rem;
  font-weight: bold;
  color: ${theme.colors.yellow};
`;

const FieldValue = styled.span`
  font-size: 0.95rem;
  color: ${theme.colors.white};
  word-break: break-word;
`;

const EmptyValue = styled.span`
  font-size: 0.95rem;
  color: ${theme.colors.gray};
`;

const WaitingRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.margins.small};
  color: ${theme.colors.gray};
  font-size: 0.9rem;
`;

const buildGoogleSearchUrl = (title: string, artist: string): string => {
  const query = [title, artist].filter(Boolean).join(" ").trim();
  return `https://www.google.com/search?q=${encodeURIComponent(query)}`;
};

const getShazamSuggestion = (
  info: ITrack["shazamInfo"],
  field: "title" | "subtitle"
): string | null => {
  const trimmed = (info?.track?.[field] ?? "").trim();
  return trimmed ? trimmed : null;
};

const hasShazamSuggestion = (info: ITrack["shazamInfo"]): boolean =>
  Boolean(
    getShazamSuggestion(info, "title") || getShazamSuggestion(info, "subtitle")
  );

const TrackShazamModal: React.FC<TrackShazamModalProps> = ({
  isOpen,
  onClose,
  track,
}): React.JSX.Element => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotification();
  const updateTrackMutation = useUpdateTrackMutation();

  const [isWaiting, setIsWaiting] = useState(false);

  const shazamQuery = useTrackShazamInfoQuery(track?.documentId, {
    enabled: isWaiting && isOpen && !!track?.documentId,
    refetchInterval: isWaiting ? SHAZAM_POLL_INTERVAL_MS : false,
  });

  // Prefer freshly fetched data once available (even when it cleared the
  // suggestion) so a re-trigger doesn't keep showing the stale prop value.
  const latestShazamInfo = shazamQuery.data
    ? shazamQuery.data.shazamInfo
    : track?.shazamInfo;
  const suggestedName = getShazamSuggestion(latestShazamInfo, "title");
  const suggestedArtist = getShazamSuggestion(latestShazamInfo, "subtitle");

  const canSearchGoogle = Boolean(
    (track?.title ?? "").trim() || (track?.artist ?? "").trim()
  );

  useEffect(() => {
    if (!isOpen) {
      setIsWaiting(false);
    }
  }, [isOpen, track?.documentId]);

  useEffect(() => {
    if (isWaiting && hasShazamSuggestion(shazamQuery.data?.shazamInfo)) {
      setIsWaiting(false);
      addNotification("Shazam suggestions updated.", "success");
    }
  }, [isWaiting, shazamQuery.data, addNotification]);

  useEffect(() => {
    if (!isWaiting) return;
    const timer = setTimeout(() => {
      setIsWaiting(false);
      addNotification(
        "Shazam is taking longer than expected. Please check back later.",
        "warning"
      );
    }, SHAZAM_POLL_TIMEOUT_MS);
    return (): void => clearTimeout(timer);
  }, [isWaiting, addNotification]);

  const handleGoogleSearch = (): void => {
    if (!track) return;
    window.open(
      buildGoogleSearchUrl(track.title ?? "", track.artist ?? ""),
      "_blank",
      "noopener,noreferrer"
    );
  };

  const handleRetrigger = (): void => {
    if (!track?.documentId) return;
    updateTrackMutation.mutate(
      {
        trackId: track.documentId,
        trackData: { shazamInfo: null, shazamAttempts: 0 },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: [QUERY_KEYS.GET_PLAYLIST_TRACKS],
          });
          setIsWaiting(true);
        },
        onError: () => {
          addNotification("Failed to re-trigger Shazam", "error");
        },
      }
    );
  };

  const isBusy = updateTrackMutation.isPending || isWaiting;

  const buttonStyle: React.CSSProperties = {
    width: "auto",
    padding: "0 20px",
    gap: 8,
    whiteSpace: "nowrap",
  };

  return (
    <UIModal
      isOpen={isOpen}
      onClose={onClose}
      title="Search Track Details"
      actions={[
        <UIButton
          key="google"
          variant="outline"
          onClick={handleGoogleSearch}
          disabled={!canSearchGoogle}
          style={buttonStyle}
        >
          <Search size={16} />
          Google Search
        </UIButton>,
        <UIButton
          key="retrigger"
          variant="primary"
          onClick={handleRetrigger}
          disabled={isBusy || !track?.documentId}
          style={buttonStyle}
        >
          {isBusy ? "Re-triggering…" : "Re-trigger Shazam"}
        </UIButton>,
      ]}
    >
      <Container>
        <Field>
          <FieldLabel>Shazam suggested name</FieldLabel>
          {suggestedName ? (
            <FieldValue>{suggestedName}</FieldValue>
          ) : (
            <EmptyValue>No suggestion</EmptyValue>
          )}
        </Field>
        <Field>
          <FieldLabel>Shazam suggested artist</FieldLabel>
          {suggestedArtist ? (
            <FieldValue>{suggestedArtist}</FieldValue>
          ) : (
            <EmptyValue>No suggestion</EmptyValue>
          )}
        </Field>
        {isWaiting && (
          <WaitingRow>
            <Spinner small inline />
            <span>Waiting for Shazam results…</span>
          </WaitingRow>
        )}
      </Container>
    </UIModal>
  );
};

export default TrackShazamModal;
